import { z } from "zod";

// JSON Resume schema with EU extensions
export const contactSchema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  phone: z.string().optional(),
  url: z.string().url().optional(),
  location: z
    .object({
      city: z.string().optional(),
      region: z.string().optional(),
      countryCode: z.string().length(2).optional(),
    })
    .optional(),
  // EU extensions
  photo: z.string().url().optional(),
  dateOfBirth: z.string().optional(),
  nationality: z.string().optional(),
});

export const workSchema = z.object({
  company: z.string(),
  position: z.string(),
  url: z.string().url().optional(),
  startDate: z.string(),
  endDate: z.string().optional(),
  summary: z.string().optional(),
  highlights: z.array(z.string()).max(20).optional(),
});

export const educationSchema = z.object({
  institution: z.string(),
  area: z.string(),
  studyType: z.string().optional(),
  startDate: z.string(),
  endDate: z.string().optional(),
  score: z.string().optional(),
  courses: z.array(z.string()).max(50).optional(),
});

export const skillSchema = z.object({
  name: z.string(),
  level: z.string().optional(),
  keywords: z.array(z.string()).max(50).optional(),
  escoUri: z.string().url().optional(), // ESCO skill URI
});

export const languageSchema = z.object({
  language: z.string(),
  fluency: z.string(), // CEFR: A1, A2, B1, B2, C1, C2, Native
});

export const certificateSchema = z.object({
  name: z.string(),
  issuer: z.string().optional(),
  date: z.string().optional(),
  url: z.string().url().optional(),
});

export const customSectionEntrySchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  description: z.string().optional(),
});

export const customSectionSchema = z.object({
  id: z.string(),
  title: z.string(),
  content: z.string(), // kept for backward compat — legacy single-entry sections
  entries: z.array(customSectionEntrySchema).max(50).optional(),
});

export const resumeDataSchema = z.object({
  basics: contactSchema,
  work: z.array(workSchema).max(50).optional(),
  education: z.array(educationSchema).max(50).optional(),
  skills: z.array(skillSchema).max(100).optional(),
  languages: z.array(languageSchema).max(20).optional(),
  certificates: z.array(certificateSchema).max(50).optional(),
  summary: z.string().optional(),
  // Meta
  templateSlug: z.string().optional(),
  countryCode: z.string().length(2).optional(),
  sectionOrder: z.array(z.string()).max(20).optional(),
  hiddenSections: z.array(z.string()).max(20).optional(),
  locale: z.string().optional(), // BCP 47 lang tag for HTML lang attribute (e.g. "en", "de")
  // Editor UX extensions (Phase 37)
  accentColor: z.string().optional(),
  fontFamily: z.enum(["sans", "serif", "mono"]).optional(),
  pageSize: z.enum(["a4", "letter"]).optional(),
  customSections: z.array(customSectionSchema).max(20).optional(),
  showSkillLevel: z.boolean().optional(),
});

export type ResumeData = z.infer<typeof resumeDataSchema>;
export type Contact = z.infer<typeof contactSchema>;
export type Work = z.infer<typeof workSchema>;
export type Education = z.infer<typeof educationSchema>;
export type Skill = z.infer<typeof skillSchema>;
export type Language = z.infer<typeof languageSchema>;
export type Certificate = z.infer<typeof certificateSchema>;
export type CustomSection = z.infer<typeof customSectionSchema>;
export type CustomSectionEntry = z.infer<typeof customSectionEntrySchema>;
